import { readProductRepositoryModeFromEnv } from './config';
import { createLocalProductRepository } from './localProductRepository';
import { createServerProductRepository } from './serverProductRepository';
import type { ProductRepository, ProductRepositoryMode } from './types';

export type { CatalogueMeta } from '../strainsApi';
export type {
  ProductRepository,
  ProductRepositoryMode,
  ProductStore,
} from './types';
export {
  resolveProductRepositoryMode,
  resolveProductRepositoryModeForEnvironment,
  readProductRepositoryModeFromEnv,
} from './config';
export { ProductRepositoryError } from './errors';
export { createLocalProductRepository } from './localProductRepository';
export { createServerProductRepository } from './serverProductRepository';
export { createMemoryProductStore } from './memoryProductStore';
export {
  createIdbProductStore,
  LOCAL_PRODUCTS_DB_NAME,
  LOCAL_PRODUCTS_DB_VERSION,
  LOCAL_PRODUCTS_STORE,
  LOCAL_PRODUCT_IMAGES_STORE,
  LOCAL_PRODUCT_IMAGES_BY_PRODUCT_INDEX,
  LOCAL_BLEND_HISTORY_STORE,
  openLocalProductsDb,
  applyLocalProductsSchemaUpgrade,
} from './idbProductStore';
export { requestToPromise, txDone } from './localProductsDb';
export {
  createFakeIndexedDbFactory,
  openFakeDb,
} from './fakeIndexedDb';
export type { FakeIndexedDbFactory } from './fakeIndexedDb';
export {
  filterAndSortStrains,
  computeCatalogueMeta,
} from './filterSort';

let cachedRepository: ProductRepository | null = null;
let cachedMode: ProductRepositoryMode | null = null;

/**
 * Central access point for the active product repository.
 * Mode is resolved once. Production is forced local; development may use
 * `VITE_PRODUCT_REPOSITORY_MODE` for deliberate legacy migration.
 */
export function getProductRepository(): ProductRepository {
  if (cachedRepository) return cachedRepository;
  const mode = readProductRepositoryModeFromEnv();
  cachedMode = mode;
  cachedRepository =
    mode === 'local'
      ? createLocalProductRepository()
      : createServerProductRepository();
  return cachedRepository;
}

/** Test helper: replace the active repository instance. */
export function setProductRepositoryForTests(
  repository: ProductRepository,
): void {
  cachedRepository = repository;
  cachedMode = repository.mode;
}

/** Test helper: clear singleton so the next call re-resolves mode. */
export function resetProductRepositoryForTests(): void {
  cachedRepository = null;
  cachedMode = null;
}

/** Currently cached mode, if any (null before first resolve). */
export function getCachedProductRepositoryMode(): ProductRepositoryMode | null {
  return cachedMode;
}
